import React, { useState, useEffect } from 'react';
import { initFirebase } from './firebase';
import { useStore } from './hooks/useStore';
import { hashPass, ini, USER_COLORS } from './utils';
import { showToast } from './components/UI';
import Dashboard from './pages/Dashboard';
import DashboardSocio from './pages/DashboardSocio';
import ChatAsistente from './pages/ChatAsistente';
import Flujo from './pages/Flujo';
import Ventas from './pages/Ventas';
import Compras from './pages/Compras';
import Costos from './pages/Costos';
import Socios from './pages/Socios';
import Prestamos from './pages/Prestamos';
import GastosVariables from './pages/GastosVariables';
import Ganancias from './pages/Ganancias';
import EstadisticasProveedores from './pages/EstadisticasProveedores';
import HistorialCostos from './pages/HistorialCostos';
import Cuentas from './pages/Cuentas';
import Historial from './pages/Historial';
import Reportes from './pages/Reportes';

// Inicializar Firebase inmediatamente al cargar el módulo
initFirebase();

const GATE_PASS = 'Farmacia2024';

const STYLES = `
  :root {
    --bg:#F7F6F2; --surf:#FFFFFF; --surf2:#F0EFE9; --surf3:#E8E6DF;
    --border:#E0DDD6; --border2:#CCC9C0;
    --tx:#18170F; --tx2:#6A6860; --tx3:#A8A49C;
    --grn:#1A6B45; --grn-l:#E6F4EC; --grn-m:#259656;
    --red:#8B2218; --red-l:#FAE8E6;
    --blu:#1A4E76; --blu-l:#EAF2F8; --blu-m:#2D7AB5;
    --yel:#7A560E; --yel-l:#FBF0D8;
    --pur:#472160; --pur-l:#F3EAF8;
    --ora:#8B3A00; --ora-l:#FEF0E6;
    --r:8px; --rl:12px;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: var(--bg); color: var(--tx); font-size: 14px; height: 100vh; overflow: hidden; }
  input, select, textarea, button { font-family: Arial, sans-serif; }
  input[type=number] { -moz-appearance: textfield; }
  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--surf2); }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
  #app-toast { position: fixed; bottom: 22px; right: 22px; background: var(--tx); color: var(--bg); border-radius: var(--r); padding: 10px 16px; font-size: 12px; font-weight: 600; z-index: 400; opacity: 0; transform: translateY(8px); transition: all .22s; pointer-events: none; }
  @media print {
    body { overflow: visible !important; background: white !important; }
    body > #root > * { display: none !important; }
    .print-section { display: block !important; page-break-inside: avoid; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

const ADMIN_NAV = [
  { id: 'dashboard',  label: 'Dashboard',                group: 'Principal' },
  { id: 'historial',  label: 'Historial movimientos',    group: 'Principal' },
  { id: 'flujo',      label: 'Flujo de fondos',          group: 'Principal' },
  { id: 'ventas',     label: 'Ventas',                   group: 'Movimientos' },
  { id: 'compras',    label: 'Compras',                  group: 'Movimientos' },
  { id: 'gastos',     label: 'Gastos variables',         group: 'Movimientos' },
  { id: 'prestamos',  label: 'Préstamos / Favores',      group: 'Movimientos' },
  { id: 'costos',     label: 'Costos fijos',             group: 'Análisis' },
  { id: 'ganancias',  label: 'Ganancias semanales',      group: 'Análisis' },
  { id: 'estadprov',  label: 'Est. proveedores',         group: 'Análisis' },
  { id: 'histcostos', label: 'Historial costos',         group: 'Análisis' },
  { id: 'cuentas',    label: 'Cuentas bancarias',        group: 'Análisis' },
  { id: 'socios',     label: 'Cuentas socios',           group: 'Análisis' },
  { id: 'reportes',   label: '📄 Reportes / Cierre',    group: 'Cierre' },
  { id: 'chat',       label: '💬 Asistente IA',          group: 'Herramientas' },
];

const SOCIO_NAV = [
  { id: 'socio_home', label: 'Mi panel',      group: 'Principal' },
  { id: 'historial',  label: 'Historial',     group: 'Principal' },
];

const PAGE_TITLES = {
  dashboard: 'Dashboard', flujo: 'Flujo de fondos', ventas: 'Ventas',
  compras: 'Compras a cuenta', costos: 'Costos fijos',
  reportes: '📄 Reportes / Cierre de Período',
  historial: 'Historial de movimientos',
  socio_home: 'Mi panel', chat: '💬 Asistente IA',
};

function hashPin(pin) {
  let h = 0;
  for (let i = 0; i < pin.length; i++) { h = ((h << 5) - h) + pin.charCodeAt(i); h = h & h; }
  return 'pin_' + h.toString(36);
}

export default function App() {
  const isMobile = window.innerWidth < 768;

  const [gateOk, setGateOk]       = useState(() => sessionStorage.getItem('gate-ok') === '1');
  const [gateInput, setGateInput] = useState('');
  const [gateError, setGateError] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [loginUser, setLoginUser]     = useState(null);
  const [loginInput, setLoginInput]   = useState('');
  const [loginError, setLoginError]   = useState('');
  const [pinInput, setPinInput]       = useState('');
  const [pinError, setPinError]       = useState(false);

  const [page, setPage]               = useState('dashboard');
  const [configOpen, setConfigOpen]   = useState(false);
  const [connected, setConnected]     = useState(false);

  const [newUserModal, setNewUserModal] = useState(false);
  const [newUserForm, setNewUserForm]   = useState({ name: '', rol: 'admin' });
  const [editUserModal, setEditUserModal] = useState(null);

  const { store, setCol, setItem, delItem } = useStore();

  useEffect(() => {
    const db = initFirebase();
    if (db) setConnected(true);
  }, []);

  function checkGate() {
    if (gateInput === GATE_PASS) { sessionStorage.setItem('gate-ok','1'); setGateOk(true); }
    else { setGateError(true); setGateInput(''); }
  }

  function tryLoginAdmin() {
    const u = loginUser; if (!u) return;
    if (!u.pass) {
      setCol('users', store.users.map(x => x.id === u.id ? {...x, pass: hashPass(loginInput)} : x));
      doLogin({...u, pass: hashPass(loginInput)});
    } else if (hashPass(loginInput) === u.pass) {
      setLoginError(''); doLogin(u);
    } else { setLoginError('Contraseña incorrecta'); setLoginInput(''); }
  }

  function appendPin(digit) {
    if (pinInput.length >= 4) return;
    const next = pinInput + digit;
    setPinInput(next); setPinError(false);
    if (next.length === 4) setTimeout(() => verifyPin(next), 120);
  }
  function deletePin() { setPinInput(p => p.slice(0,-1)); setPinError(false); }

  function verifyPin(pin) {
    const u = loginUser; if (!u) return;
    if (!u.pin) {
      setCol('users', store.users.map(x => x.id === u.id ? {...x, pin: hashPin(pin)} : x));
      doLogin({...u, pin: hashPin(pin)});
    } else if (hashPin(pin) === u.pin) {
      doLogin(u);
    } else { setPinError(true); setPinInput(''); }
  }

  function doLogin(u) {
    setCurrentUser(u); setLoginUser(null);
    setLoginInput(''); setPinInput('');
    setPage(u.rol === 'socio' ? 'socio_home' : 'dashboard');
  }

  function selectUser(u) {
    if (loginUser?.id === u.id) { setLoginUser(null); setLoginInput(''); setPinInput(''); }
    else { setLoginUser(u); setLoginInput(''); setPinInput(''); setLoginError(''); setPinError(false); }
  }

  function confirmAddUser() {
    const name = newUserForm.name.trim(); if (!name) return;
    setCol('users', [...store.users, { id:'u'+Date.now(), name, color: USER_COLORS[store.users.length % USER_COLORS.length], rol: newUserForm.rol }]);
    setNewUserModal(false);
  }

  const NAV = currentUser?.rol === 'socio' ? SOCIO_NAV : ADMIN_NAV;
  const groups = NAV.reduce((acc, item) => { if (!acc[item.group]) acc[item.group]=[]; acc[item.group].push(item); return acc; }, {});
  const pageProps = { store, setItem, delItem, setCol, currentUser };
  const isSocio = currentUser?.rol === 'socio';

  // ── GATE ──
  if (!gateOk) return (
    <><style>{STYLES}</style>
    <div style={{position:'fixed',inset:0,background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'var(--surf)',border:'1px solid var(--border)',borderRadius:16,padding:32,width:320,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{width:44,height:44,background:'var(--grn)',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:22,fontWeight:700,margin:'0 auto 16px'}}>F</div>
        <div style={{fontSize:18,fontWeight:700,textAlign:'center',marginBottom:4}}>Farmacia Yacanto</div>
        <div style={{fontSize:12,color:'var(--tx3)',textAlign:'center',marginBottom:20}}>Panel de gestión</div>
        <input type="password" value={gateInput} onChange={e=>{setGateInput(e.target.value);setGateError(false);}}
          onKeyDown={e=>e.key==='Enter'&&checkGate()} placeholder="Contraseña de acceso..."
          style={{width:'100%',padding:'11px 14px',borderRadius:10,border:`1.5px solid ${gateError?'var(--red)':'var(--border2)'}`,fontSize:15,marginBottom:8,background:'var(--surf)',color:'var(--tx)',outline:'none'}} />
        {gateError && <div style={{fontSize:11,color:'var(--red)',marginBottom:8}}>Contraseña incorrecta</div>}
        <button onClick={checkGate} style={{width:'100%',padding:13,background:'var(--grn)',color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer'}}>Ingresar</button>
      </div>
    </div></>
  );

  // ── MOBILE → solo chat ──
  if (isMobile) return (
    <><style>{STYLES}</style>
    <div id="app-toast"></div>
    <div style={{height:'100vh',display:'flex',flexDirection:'column',background:'var(--bg)'}}>
      <ChatAsistente {...pageProps} currentUser={currentUser || {name:'Usuario',rol:'admin'}} />
    </div></>
  );

  // ── LOGIN ──
  if (!currentUser) return (
    <><style>{STYLES}</style>
    <div style={{position:'fixed',inset:0,background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'var(--surf)',border:'1px solid var(--border)',borderRadius:16,padding:28,width:380,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <div style={{width:36,height:36,background:'var(--grn)',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:18}}>F</div>
          <div>
            <div style={{fontSize:15,fontWeight:700}}>Farmacia Yacanto</div>
            <div style={{fontSize:11,color:'var(--tx3)'}}>Seleccioná tu usuario</div>
          </div>
        </div>

        <div style={{marginBottom:14}}>
          {store.users.length===0 && <div style={{textAlign:'center',padding:20,color:'var(--tx3)',fontSize:12}}>Agregá el primer usuario abajo</div>}
          {store.users.map(u => (
            <div key={u.id} style={{marginBottom:6}}>
              <div onClick={()=>selectUser(u)}
                style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:10,cursor:'pointer',
                  background: loginUser?.id===u.id ? (u.rol==='socio'?'#EEE8F4':'var(--grn-l)') : 'var(--surf2)',
                  border:`1px solid ${loginUser?.id===u.id?(u.rol==='socio'?'var(--pur)':'var(--grn)'):'transparent'}`}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:u.color,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700}}>{ini(u.name)}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600}}>{u.name}</div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:11,color:'var(--tx3)'}}>
                      {u.rol==='socio'?'👤 Socia · PIN numérico':'⚙ Admin · contraseña'}
                      {u.rol==='socio'&&!u.pin?' · Crear PIN':u.rol!=='socio'&&!u.pass?' · Crear contraseña':''}
                    </span>
                    <button onClick={e=>{e.stopPropagation();setEditUserModal(u);}} style={{fontSize:10,padding:'1px 6px',borderRadius:4,border:'1px solid var(--border2)',background:'var(--surf3)',cursor:'pointer',color:'var(--tx3)'}}>✏</button>
                  </div>
                </div>
              </div>

              {loginUser?.id===u.id && (
                <div style={{padding:'14px 14px 12px',background:'var(--surf2)',borderRadius:'0 0 10px 10px',borderTop:'1px solid var(--border)'}}>
                  {u.rol==='socio' ? (
                    // ── PIN KEYPAD ──
                    <div style={{textAlign:'center'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--pur)',marginBottom:12}}>
                        {u.pin?'Ingresá tu PIN':'Creá tu PIN de 4 dígitos'}
                      </div>
                      <div style={{display:'flex',justifyContent:'center',gap:12,marginBottom:14}}>
                        {[0,1,2,3].map(i=>(
                          <div key={i} style={{width:14,height:14,borderRadius:'50%',
                            border:`2px solid ${pinError?'var(--red)':'var(--pur)'}`,
                            background:pinInput.length>i?(pinError?'var(--red)':'var(--pur)'):'transparent',
                            transition:'all .15s'}} />
                        ))}
                      </div>
                      {pinError && <div style={{fontSize:11,color:'var(--red)',marginBottom:10}}>PIN incorrecto, intentá de nuevo</div>}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(3,60px)',gap:8,justifyContent:'center',margin:'0 auto'}}>
                        {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((d,i)=>(
                          d===''?<div key={i}/>:
                          <button key={i} onClick={()=>d==='⌫'?deletePin():appendPin(String(d))}
                            style={{width:60,height:60,borderRadius:'50%',border:'1.5px solid var(--border2)',
                              background:'var(--surf)',fontSize:d==='⌫'?16:20,fontWeight:700,
                              cursor:'pointer',color:'var(--tx)',transition:'background .1s'}}
                            onMouseDown={e=>e.currentTarget.style.background='var(--surf3)'}
                            onMouseUp={e=>e.currentTarget.style.background='var(--surf)'}>
                            {d}
                          </button>
                        ))}
                      </div>
                      <button onClick={()=>{setLoginUser(null);setPinInput('');}}
                        style={{marginTop:12,fontSize:12,color:'var(--tx3)',background:'none',border:'none',cursor:'pointer'}}>
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    // ── PASSWORD ──
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--tx2)',marginBottom:6}}>{u.pass?'Ingresá tu contraseña':'Creá tu contraseña'}</div>
                      <input type="password" value={loginInput} autoFocus
                        onChange={e=>{setLoginInput(e.target.value);setLoginError('');}}
                        onKeyDown={e=>{if(e.key==='Enter')tryLoginAdmin();if(e.key==='Escape'){setLoginUser(null);setLoginInput('');}}}
                        placeholder="Contraseña..."
                        style={{width:'100%',padding:'8px 10px',borderRadius:7,border:`1.5px solid ${loginError?'var(--red)':'var(--border2)'}`,fontSize:14,marginBottom:6,background:'var(--surf)',color:'var(--tx)',outline:'none',boxSizing:'border-box'}} />
                      {loginError && <div style={{fontSize:11,color:'var(--red)',marginBottom:6}}>{loginError}</div>}
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={tryLoginAdmin} style={{flex:1,padding:'8px',background:'var(--grn)',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:700,cursor:'pointer'}}>{u.pass?'Entrar':'Crear y entrar'}</button>
                        <button onClick={()=>{setLoginUser(null);setLoginInput('');}} style={{padding:'8px 12px',background:'var(--surf3)',color:'var(--tx2)',border:'none',borderRadius:7,fontSize:13,cursor:'pointer'}}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{borderTop:'1px solid var(--border)',paddingTop:12}}>
          <button onClick={()=>{setNewUserForm({name:'',rol:'admin'});setNewUserModal(true);}}
            style={{width:'100%',padding:'8px',background:'var(--surf2)',color:'var(--tx2)',border:'1px solid var(--border2)',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
            + Agregar usuario
          </button>
        </div>
      </div>
    </div>
    <UserModals {...{newUserModal,setNewUserModal,newUserForm,setNewUserForm,confirmAddUser,editUserModal,setEditUserModal,store,setCol}} />
    </>
  );

  // ── APP ──
  return (
    <><style>{STYLES}</style>
    <div id="app-toast"></div>
    <div style={{display:'flex',height:'100vh',overflow:'hidden'}}>

      {/* Sidebar */}
      <div style={{width:isSocio?200:220,background:'var(--surf)',borderRight:'1px solid var(--border)',display:'flex',flexDirection:'column',flexShrink:0,overflowY:'auto'}}>
        <div style={{padding:'16px 14px 12px',borderBottom:'1px solid var(--border)'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,fontWeight:700,fontSize:14}}>
            <div style={{width:28,height:28,background:isSocio?currentUser.color:'var(--grn)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700}}>F</div>
            Farmacia
          </div>
        </div>
        <div style={{padding:'10px 12px 8px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:26,height:26,borderRadius:'50%',background:currentUser.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:'#fff'}}>{ini(currentUser.name)}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{currentUser.name}</div>
            <div style={{fontSize:10,color:connected?'var(--grn)':'var(--tx3)',display:'flex',alignItems:'center',gap:4}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:connected?'var(--grn)':'var(--tx3)',flexShrink:0}}></span>
              {connected?'Sincronizado':'Local'}
            </div>
          </div>
        </div>

        {Object.entries(groups).map(([group,items])=>(
          <div key={group} style={{padding:'10px 8px 2px'}}>
            <div style={{fontSize:9,fontWeight:700,color:'var(--tx3)',padding:'0 8px 5px',letterSpacing:'.1em',textTransform:'uppercase'}}>{group}</div>
            {items.map(item=>(
              <div key={item.id} onClick={()=>setPage(item.id)}
                style={{display:'flex',alignItems:'center',gap:8,padding:'7px 8px',borderRadius:8,cursor:'pointer',fontSize:12.5,
                  color:page===item.id?(isSocio?'var(--pur)':'var(--grn)'):'var(--tx2)',
                  background:page===item.id?(isSocio?'var(--pur-l)':'var(--grn-l)'):'transparent',
                  fontWeight:page===item.id?600:400,transition:'all .12s'}}
                onMouseEnter={e=>{if(page!==item.id)e.currentTarget.style.background='var(--surf2)';}}
                onMouseLeave={e=>{if(page!==item.id)e.currentTarget.style.background='transparent';}}>
                {item.label}
              </div>
            ))}
          </div>
        ))}

        <div style={{marginTop:'auto',padding:'8px 8px 12px',borderTop:'1px solid var(--border)'}}>
          {!connected && !isSocio && (
            <div onClick={()=>setConfigOpen(true)}
              style={{display:'flex',alignItems:'center',gap:8,padding:'7px 8px',borderRadius:8,cursor:'pointer',fontSize:12,color:'var(--yel)',background:'var(--yel-l)',marginBottom:4,fontWeight:600}}>
              ⚙ Configurar Firebase
            </div>
          )}
          <div onClick={()=>setCurrentUser(null)}
            style={{display:'flex',alignItems:'center',gap:8,padding:'7px 8px',borderRadius:8,cursor:'pointer',fontSize:12,color:'var(--tx3)'}}>
            Cerrar sesión
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'0 22px',height:52,borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10,background:'var(--surf)',flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:700,flex:1}}>{PAGE_TITLES[page]||page}</div>
          {isSocio && <div style={{fontSize:11,color:'var(--pur)',background:'var(--pur-l)',padding:'4px 10px',borderRadius:20,fontWeight:600}}>Panel Socia</div>}
        </div>
        <div style={{flex:1,overflowY:page==='chat'?'hidden':'auto',padding:page==='chat'?0:20,display:'flex',flexDirection:'column'}}>
          {!isSocio && page==='dashboard'  && <Dashboard {...pageProps} />}
          {!isSocio && page==='flujo'       && <Flujo {...pageProps} />}
          {!isSocio && page==='ventas'      && <Ventas {...pageProps} />}
          {!isSocio && page==='compras'     && <Compras {...pageProps} />}
          {!isSocio && page==='costos'      && <Costos {...pageProps} />}
          {!isSocio && page==='ganancias'   && <Ganancias {...pageProps} />}
          {!isSocio && page==='estadprov'   && <EstadisticasProveedores {...pageProps} />}
          {!isSocio && page==='histcostos'  && <HistorialCostos {...pageProps} />}
          {!isSocio && page==='cuentas'     && <Cuentas {...pageProps} />}
          {!isSocio && page==='socios'      && <Socios {...pageProps} />}
          {!isSocio && page==='gastos'      && <GastosVariables {...pageProps} />}
          {!isSocio && page==='prestamos'   && <Prestamos {...pageProps} />}
          {!isSocio && page==='reportes'    && <Reportes {...pageProps} />}
          {!isSocio && page==='chat'        && <ChatAsistente {...pageProps} currentUser={currentUser} />}
          {isSocio  && page==='socio_home'  && <DashboardSocio {...pageProps} currentUser={currentUser} />}
          {page==='historial' && <Historial {...pageProps} readOnly={isSocio} />}
        </div>
      </div>
    </div>

    {/* Firebase config */}
    {configOpen && !isSocio && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
        <div style={{background:'var(--surf)',borderRadius:14,padding:24,width:420}}>
          <h3 style={{marginBottom:16,fontSize:15,fontWeight:700}}>Configurar Firebase</h3>
          {[['API Key','fb-api','AIza...'],['Database URL','fb-db','https://...firebaseio.com'],['Project ID','fb-pid','mi-proyecto']].map(([label,key,ph])=>(
            <div key={key} style={{marginBottom:10}}>
              <label style={{fontSize:10,fontWeight:700,color:'var(--tx2)',textTransform:'uppercase',display:'block',marginBottom:4}}>{label}</label>
              <input defaultValue={localStorage.getItem(key)||''} id={`cfg-${key}`} placeholder={ph}
                style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--border2)',fontSize:13,background:'var(--surf)',color:'var(--tx)',outline:'none'}} />
            </div>
          ))}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:14}}>
            <button onClick={()=>setConfigOpen(false)} style={{padding:'8px 16px',borderRadius:8,border:'1px solid var(--border2)',background:'var(--surf2)',cursor:'pointer',fontSize:12,fontWeight:600}}>Cancelar</button>
            <button onClick={()=>{
              ['fb-api','fb-db','fb-pid'].forEach(k=>{const el=document.getElementById(`cfg-${k}`);if(el?.value)localStorage.setItem(k,el.value);});
              setConfigOpen(false);showToast('Guardado — recargando...');setTimeout(()=>window.location.reload(),1200);
            }} style={{padding:'8px 16px',borderRadius:8,border:'none',background:'var(--grn)',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700}}>Guardar y conectar</button>
          </div>
        </div>
      </div>
    )}

    <UserModals {...{newUserModal,setNewUserModal,newUserForm,setNewUserForm,confirmAddUser,editUserModal,setEditUserModal,store,setCol}} />
    </>
  );
}

function UserModals({newUserModal,setNewUserModal,newUserForm,setNewUserForm,confirmAddUser,editUserModal,setEditUserModal,store,setCol}) {
  return (<>
    {newUserModal && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300}}>
        <div style={{background:'var(--surf)',borderRadius:14,padding:24,width:340,boxShadow:'0 8px 40px rgba(0,0,0,.15)'}}>
          <h3 style={{fontSize:15,fontWeight:700,marginBottom:16}}>Nuevo usuario</h3>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:10,fontWeight:700,color:'var(--tx2)',textTransform:'uppercase',display:'block',marginBottom:5}}>Nombre</label>
            <input autoFocus value={newUserForm.name} onChange={e=>setNewUserForm(f=>({...f,name:e.target.value}))}
              onKeyDown={e=>e.key==='Enter'&&confirmAddUser()} placeholder="Nombre del usuario..."
              style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1px solid var(--border2)',fontSize:14,background:'var(--surf)',color:'var(--tx)',outline:'none',boxSizing:'border-box'}} />
          </div>
          <div style={{marginBottom:16}}>
            <label style={{fontSize:10,fontWeight:700,color:'var(--tx2)',textTransform:'uppercase',display:'block',marginBottom:8}}>Rol</label>
            <div style={{display:'flex',gap:8}}>
              {[['admin','Administrador','Acceso completo'],['socio','Socia','Panel reducido + PIN']].map(([val,label,sub])=>(
                <button key={val} onClick={()=>setNewUserForm(f=>({...f,rol:val}))}
                  style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${newUserForm.rol===val?(val==='socio'?'var(--pur)':'var(--grn)'):'var(--border2)'}`,background:newUserForm.rol===val?(val==='socio'?'var(--pur-l)':'var(--grn-l)'):'var(--surf2)',cursor:'pointer',textAlign:'center'}}>
                  <div style={{fontSize:12,fontWeight:700,color:newUserForm.rol===val?(val==='socio'?'var(--pur)':'var(--grn)'):'var(--tx)'}}>{label}</div>
                  <div style={{fontSize:10,color:'var(--tx3)',marginTop:2}}>{sub}</div>
                </button>
              ))}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>setNewUserModal(false)} style={{flex:1,padding:10,borderRadius:8,border:'1px solid var(--border2)',background:'var(--surf2)',cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--tx)'}}>Cancelar</button>
            <button onClick={confirmAddUser} style={{flex:1,padding:10,borderRadius:8,border:'none',background:'var(--grn)',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700}}>Agregar</button>
          </div>
        </div>
      </div>
    )}
    {editUserModal && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300}}>
        <div style={{background:'var(--surf)',borderRadius:14,padding:24,width:340,boxShadow:'0 8px 40px rgba(0,0,0,.15)'}}>
          <h3 style={{fontSize:15,fontWeight:700,marginBottom:16}}>Editar — {editUserModal.name}</h3>
          <div style={{marginBottom:16}}>
            <label style={{fontSize:10,fontWeight:700,color:'var(--tx2)',textTransform:'uppercase',display:'block',marginBottom:8}}>Rol</label>
            <div style={{display:'flex',gap:8}}>
              {[['admin','Administrador'],['socio','Socia']].map(([val,label])=>(
                <button key={val} onClick={()=>setEditUserModal(u=>({...u,rol:val}))}
                  style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${editUserModal.rol===val?'var(--grn)':'var(--border2)'}`,background:editUserModal.rol===val?'var(--grn-l)':'var(--surf2)',cursor:'pointer',textAlign:'center',fontSize:12,fontWeight:700,color:editUserModal.rol===val?'var(--grn)':'var(--tx)'}}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{fontSize:10,fontWeight:700,color:'var(--tx2)',textTransform:'uppercase',display:'block',marginBottom:8}}>Credencial</label>
            <button onClick={()=>{
              setCol('users',store.users.map(u=>u.id===editUserModal.id?{...u,pass:undefined,pin:undefined}:u));
              showToast('PIN/contraseña reseteado'); setEditUserModal(null);
            }} style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid var(--border2)',background:'var(--surf2)',cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--red)'}}>
              🔑 Resetear PIN / contraseña
            </button>
          </div>
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <button onClick={()=>setEditUserModal(null)} style={{flex:1,padding:10,borderRadius:8,border:'1px solid var(--border2)',background:'var(--surf2)',cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--tx)'}}>Cancelar</button>
            <button onClick={()=>{
              setCol('users',store.users.map(u=>u.id===editUserModal.id?{...u,rol:editUserModal.rol}:u));
              showToast('Usuario actualizado'); setEditUserModal(null);
            }} style={{flex:1,padding:10,borderRadius:8,border:'none',background:'var(--grn)',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700}}>Guardar</button>
          </div>
          <button onClick={()=>{
            if(!window.confirm(`¿Eliminar a ${editUserModal.name}?`)) return;
            setCol('users', store.users.filter(u=>u.id!==editUserModal.id));
            showToast('Usuario eliminado'); setEditUserModal(null);
          }} style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid var(--red)',background:'var(--red-l)',cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--red)'}}>🗑 Eliminar usuario</button>
        </div>
      </div>
    )}
  </>);
}
